import type { IFilesystem, FileMetadata } from '@livx.cc/wcli/core';
import type { AgentTool } from './tools';

/**
 * Copy-on-write overlay over any base IFilesystem — the base is never touched until
 * you `commit()`. Writes/deletes/mkdirs accumulate in an in-memory overlay; reads see
 * overlay-then-base. This unlocks capabilities the swappable-VFS architecture makes
 * cheap and that a real agent wants:
 *   - `snapshot()` / `rollback(token)` — checkpoint before a risky multi-file change and
 *     undo it instantly (the `Checkpoint`/`Rollback` agent tools sit on these);
 *   - `diff()` — exactly what changed vs the base (added/modified/deleted);
 *   - `commit()` — flush the overlay down into the base when you're happy.
 * Edge-safe: pure IFilesystem, no node builtins.
 */
interface OverlayState {
  writes: Map<string, string>; // path -> content (created/modified)
  deletes: Set<string>;        // tombstones hiding a base path
  dirs: Set<string>;           // overlay-created directories
}

export class OverlayFilesystem implements IFilesystem {
  private st: OverlayState = { writes: new Map(), deletes: new Set(), dirs: new Set() };
  private snapshots = new Map<string, OverlayState>();
  private seq = 0;

  constructor(private base: IFilesystem) {}

  // ---- path plumbing: delegate to the base so normalization/cwd are identical ----
  resolvePath(path: string, cwd?: string): string { return this.base.resolvePath(path, cwd ?? this.base.getCwd()); }
  getCwd(): string { return this.base.getCwd(); }
  setCwd(path: string): void { this.base.setCwd(path); }
  private abs(p: string): string { return this.resolvePath(p); }
  private parent(abs: string): string { const i = abs.lastIndexOf('/'); return i <= 0 ? '/' : abs.slice(0, i); }
  private name(abs: string): string { return abs.slice(abs.lastIndexOf('/') + 1); }

  // ---- reads (overlay wins; tombstones hide the base) ----
  async readFile(path: string): Promise<string> {
    const p = this.abs(path);
    if (this.st.deletes.has(p)) throw new Error(`File not found: ${path}`);
    if (this.st.writes.has(p)) return this.st.writes.get(p)!;
    return this.base.readFile(p);
  }

  async exists(path: string): Promise<boolean> {
    const p = this.abs(path);
    if (this.st.deletes.has(p)) return false;
    if (this.st.writes.has(p) || this.st.dirs.has(p)) return true;
    return this.base.exists(p);
  }

  async isFile(path: string): Promise<boolean> {
    const p = this.abs(path);
    if (this.st.deletes.has(p)) return false;
    if (this.st.writes.has(p)) return true;
    if (this.st.dirs.has(p)) return false;
    return this.base.isFile(p);
  }

  async isDirectory(path: string): Promise<boolean> {
    const p = this.abs(path);
    if (this.st.deletes.has(p)) return false;
    if (this.st.dirs.has(p)) return true;
    if (this.st.writes.has(p)) return false;
    return this.base.isDirectory(p);
  }

  async stat(path: string): Promise<FileMetadata> {
    const p = this.abs(path);
    if (this.st.deletes.has(p)) throw new Error(`File not found: ${path}`);
    if (this.st.writes.has(p)) {
      const size = this.st.writes.get(p)!.length;
      return { created: new Date(0), modified: new Date(0), size, permissions: '-rw-r--r--', isExecutable: false };
    }
    if (this.st.dirs.has(p)) return { created: new Date(0), modified: new Date(0), size: 0, permissions: 'drwxr-xr-x', isExecutable: false };
    return this.base.stat(p);
  }

  /** Merge base entries (minus tombstones) with overlay-created children of `path`. */
  async readDir(path: string): Promise<string[]> {
    const p = this.abs(path);
    if (await this.isFile(p)) throw new Error(`Not a directory: ${path}`); // match base contract (don't swallow → return [])
    const names = new Set<string>();
    try { for (const n of await this.base.readDir(p)) names.add(n); } catch { /* base dir may not exist; overlay may still have children */ }
    for (const w of [...this.st.writes.keys(), ...this.st.dirs]) if (this.parent(w) === p) names.add(this.name(w));
    for (const d of this.st.deletes) if (this.parent(d) === p) names.delete(this.name(d));
    if (names.size === 0 && !(await this.exists(p)) && p !== '/') throw new Error(`Directory not found: ${path}`);
    return [...names].sort();
  }

  // ---- writes (recorded in the overlay; base untouched until commit) ----
  async writeFile(path: string, content: string): Promise<void> {
    const p = this.abs(path);
    if (!(await this.exists(this.parent(p)))) throw new Error(`Parent directory does not exist: ${path}`);
    this.st.deletes.delete(p);
    this.st.writes.set(p, content);
  }

  async createDir(path: string): Promise<void> {
    const p = this.abs(path);
    if (await this.exists(p)) throw new Error(`File or directory already exists: ${path}`);
    if (!(await this.exists(this.parent(p)))) throw new Error(`Parent directory does not exist: ${path}`);
    this.st.deletes.delete(p);
    this.st.dirs.add(p);
  }

  async deleteFile(path: string): Promise<void> {
    const p = this.abs(path);
    if (!(await this.exists(p))) throw new Error(`File not found: ${path}`);
    if (await this.isDirectory(p) && (await this.readDir(p)).length > 0) throw new Error(`Directory not empty: ${path}`);
    this.st.writes.delete(p);
    this.st.dirs.delete(p);
    this.st.deletes.add(p); // tombstone (in case it exists in the base)
  }

  // ---- checkpointing ----
  private clone(s: OverlayState): OverlayState {
    return { writes: new Map(s.writes), deletes: new Set(s.deletes), dirs: new Set(s.dirs) };
  }

  /** Capture the current overlay; returns a token to `rollback(token)` to. */
  snapshot(label?: string): string {
    const token = label ?? `snap-${++this.seq}`;
    this.snapshots.set(token, this.clone(this.st));
    return token;
  }

  /** Discard all changes since the named snapshot (or the most recent if omitted). */
  rollback(token?: string): void {
    const key = token ?? [...this.snapshots.keys()].pop();
    if (key == null || !this.snapshots.has(key)) throw new Error(`No snapshot '${token ?? '(latest)'}'`);
    this.st = this.clone(this.snapshots.get(key)!);
  }

  /** Paths changed vs the base since this overlay was created (added = not in base, modified = was). */
  async diff(): Promise<{ added: string[]; modified: string[]; deleted: string[] }> {
    const added: string[] = [], modified: string[] = [];
    for (const p of this.st.writes.keys()) ((await this.base.exists(p)) ? modified : added).push(p);
    return { added: added.sort(), modified: modified.sort(), deleted: [...this.st.deletes].sort() };
  }

  /** Flush the overlay into the base (writes, mkdirs, deletes), then clear the overlay. */
  async commit(): Promise<void> {
    for (const d of this.st.dirs) if (!(await this.base.exists(d))) await this.base.createDir(d).catch(() => {});
    for (const [p, c] of this.st.writes) await this.base.writeFile(p, c);
    for (const p of this.st.deletes) if (await this.base.exists(p)) await this.base.deleteFile(p).catch(() => {});
    this.st = { writes: new Map(), deletes: new Set(), dirs: new Set() };
    this.snapshots.clear();
  }
}

/** Duck-typed checkpoint capability (so the tools work over any snapshot/rollback FS). */
interface Checkpointable { snapshot(label?: string): string; rollback(token?: string): void; }
const asCheckpointable = (fs: any): Checkpointable | null =>
  typeof fs?.snapshot === 'function' && typeof fs?.rollback === 'function' ? fs : null;
const NEEDS_OVERLAY = 'Error: checkpointing requires an OverlayFilesystem (run with `checkpoints: true` over one).';

/** `Checkpoint` tool — snapshot the VFS before a risky multi-step change. */
export const checkpointTool: AgentTool = {
  name: 'Checkpoint',
  description: 'Snapshot the filesystem before a risky or exploratory multi-step change, so you can undo it. Returns a token to pass to Rollback if you need to revert.',
  parameters: { type: 'object', properties: { label: { type: 'string', description: 'optional name for the checkpoint' } } },
  async run({ label }, ctx) {
    const fs = asCheckpointable(ctx.fs);
    if (!fs) return NEEDS_OVERLAY;
    return `Checkpoint '${fs.snapshot(label ? String(label) : undefined)}' created — call Rollback with it to undo everything since.`;
  },
};

/** `Rollback` tool — undo all VFS changes since a Checkpoint. */
export const rollbackTool: AgentTool = {
  name: 'Rollback',
  description: 'Undo ALL filesystem changes made since a Checkpoint. Pass the token from Checkpoint, or omit to revert to the most recent one. Use when an approach went wrong.',
  parameters: { type: 'object', properties: { to: { type: 'string', description: 'checkpoint token (omit for most recent)' } } },
  async run({ to }, ctx) {
    const fs = asCheckpointable(ctx.fs);
    if (!fs) return NEEDS_OVERLAY;
    try { fs.rollback(to ? String(to) : undefined); return `Rolled back to checkpoint '${to ?? '(latest)'}'.`; }
    catch (e: any) { return `Error: ${e?.message ?? e}`; }
  },
};

export const checkpointTools = (): AgentTool[] => [checkpointTool, rollbackTool];
